/**
 * RangeInputWithSlider - Hybrid range selector with visual slider + editable number inputs
 *
 * Combines the visual feedback of a slider with precise number inputs with +/- stepper buttons.
 * Mobile-first design that works well on both mobile and desktop.
 */

import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

interface RangeInputWithSliderProps {
  /** Label for the range input */
  label: string;
  /** Unit suffix (e.g., "min", "TSS") */
  unit?: string;
  /** Current min value */
  minValue: number;
  /** Current max value */
  maxValue: number;
  /** Minimum allowed value */
  min: number;
  /** Maximum allowed value */
  max: number;
  /** Step increment for stepper buttons */
  step?: number;
  /** Slider marks */
  marks?: { value: number; label: string }[];
  /** Callback when value changes (during interaction) */
  onChange: (min: number, max: number) => void;
  /** Callback when value change is committed (final value) */
  onChangeCommitted?: (min: number, max: number) => void;
  /** Aria label for accessibility */
  ariaLabel?: string;
}

// Stepper Input component - extracted outside to prevent recreation on every render
interface StepperInputProps {
  value: number;
  step: number;
  isMobile: boolean;
  onStep: (delta: number) => void;
  onInputChange: (value: string) => void;
  onInputBlur: () => void;
  inputAriaLabel: string;
  decrementAriaLabel: string;
  incrementAriaLabel: string;
  disableDecrement: boolean;
  disableIncrement: boolean;
}

const StepperInput = memo<StepperInputProps>(
  ({
    value,
    step,
    isMobile,
    onStep,
    onInputChange,
    onInputBlur,
    inputAriaLabel,
    decrementAriaLabel,
    incrementAriaLabel,
    disableDecrement,
    disableIncrement,
  }) => {
    // Local editing state for text input
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');

    const displayValue = isEditing ? editValue : value.toString();

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsEditing(true);
      setEditValue(e.target.value);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditValue(e.target.value);
      onInputChange(e.target.value);
    };

    const handleBlur = () => {
      setIsEditing(false);
      onInputBlur();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        (e.target as HTMLInputElement).blur();
      }
    };

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          px: 0.5,
          py: 0.25,
        }}
      >
        <IconButton
          size="small"
          onClick={() => onStep(-step)}
          disabled={disableDecrement}
          aria-label={decrementAriaLabel}
          sx={{
            p: 0.5,
            minWidth: 28,
            minHeight: 28,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <TextField
          value={displayValue}
          onFocus={handleFocus}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          size="small"
          inputProps={{
            'aria-label': inputAriaLabel,
            style: {
              textAlign: 'center',
              padding: '4px 2px',
              width: isMobile ? 40 : 45,
              fontSize: isMobile ? '0.875rem' : '0.9rem',
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
            },
            '& .MuiInputBase-input': {
              p: 0.5,
            },
          }}
        />
        <IconButton
          size="small"
          onClick={() => onStep(step)}
          disabled={disableIncrement}
          aria-label={incrementAriaLabel}
          sx={{
            p: 0.5,
            minWidth: 28,
            minHeight: 28,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }
);

StepperInput.displayName = 'StepperInput';

export const RangeInputWithSlider: React.FC<RangeInputWithSliderProps> = ({
  label,
  unit = '',
  minValue,
  maxValue,
  min,
  max,
  step = 5,
  marks,
  onChange,
  onChangeCommitted,
  ariaLabel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Local state for optimistic UI updates
  const [localMin, setLocalMin] = useState(minValue);
  const [localMax, setLocalMax] = useState(maxValue);

  // Track pending input values for text editing
  const pendingMinRef = useRef<string | null>(null);
  const pendingMaxRef = useRef<string | null>(null);

  // Sync local state when props change from parent (only if not editing)
  useEffect(() => {
    if (pendingMinRef.current === null) {
      setLocalMin(minValue);
    }
  }, [minValue]);

  useEffect(() => {
    if (pendingMaxRef.current === null) {
      setLocalMax(maxValue);
    }
  }, [maxValue]);

  // Debounce timer for commits
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Debounced commit
  const debouncedCommit = useCallback(
    (newMin: number, newMax: number) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        onChangeCommitted?.(newMin, newMax);
      }, 300);
    },
    [onChangeCommitted]
  );

  // Handle slider change
  const handleSliderChange = useCallback((_event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setLocalMin(newValue[0]);
      setLocalMax(newValue[1]);
    }
  }, []);

  // Handle slider change committed
  const handleSliderChangeCommitted = useCallback(
    (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
      if (Array.isArray(newValue)) {
        onChange(newValue[0], newValue[1]);
        onChangeCommitted?.(newValue[0], newValue[1]);
      }
    },
    [onChange, onChangeCommitted]
  );

  // Handle stepper clicks - instant local update
  const handleStepMin = useCallback(
    (delta: number) => {
      setLocalMin(prev => {
        const newMin = Math.max(min, Math.min(prev + delta, localMax));
        // Defer parent notification to not block UI
        setTimeout(() => {
          onChange(newMin, localMax);
          debouncedCommit(newMin, localMax);
        }, 0);
        return newMin;
      });
    },
    [min, localMax, onChange, debouncedCommit]
  );

  const handleStepMax = useCallback(
    (delta: number) => {
      setLocalMax(prev => {
        const newMax = Math.max(localMin, Math.min(prev + delta, max));
        // Defer parent notification to not block UI
        setTimeout(() => {
          onChange(localMin, newMax);
          debouncedCommit(localMin, newMax);
        }, 0);
        return newMax;
      });
    },
    [max, localMin, onChange, debouncedCommit]
  );

  // Handle text input changes (while typing)
  const handleMinInputChange = useCallback((value: string) => {
    pendingMinRef.current = value;
  }, []);

  const handleMaxInputChange = useCallback((value: string) => {
    pendingMaxRef.current = value;
  }, []);

  // Handle text input blur (commit)
  const handleMinInputBlur = useCallback(() => {
    const value = pendingMinRef.current;
    pendingMinRef.current = null;

    if (value !== null) {
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed)) {
        const clamped = Math.max(min, Math.min(parsed, localMax));
        setLocalMin(clamped);
        onChange(clamped, localMax);
        onChangeCommitted?.(clamped, localMax);
      }
    }
  }, [min, localMax, onChange, onChangeCommitted]);

  const handleMaxInputBlur = useCallback(() => {
    const value = pendingMaxRef.current;
    pendingMaxRef.current = null;

    if (value !== null) {
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed)) {
        const clamped = Math.max(localMin, Math.min(parsed, max));
        setLocalMax(clamped);
        onChange(localMin, clamped);
        onChangeCommitted?.(localMin, clamped);
      }
    }
  }, [max, localMin, onChange, onChangeCommitted]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: isMobile ? '100%' : 220,
      }}
    >
      {/* Label */}
      <Typography variant="caption" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>

      {/* Slider - uses local state for instant feedback */}
      <Slider
        value={[localMin, localMax]}
        onChange={handleSliderChange}
        onChangeCommitted={handleSliderChangeCommitted}
        min={min}
        max={max}
        step={step}
        marks={marks}
        disableSwap
        valueLabelDisplay="auto"
        valueLabelFormat={value => `${value}${unit}`}
        aria-label={ariaLabel || label}
        sx={{
          mx: 1,
          '& .MuiSlider-markLabel': {
            fontSize: '0.65rem',
          },
        }}
      />

      {/* Number inputs with steppers */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          flexWrap: 'nowrap',
        }}
      >
        <StepperInput
          value={localMin}
          step={step}
          isMobile={isMobile}
          onStep={handleStepMin}
          onInputChange={handleMinInputChange}
          onInputBlur={handleMinInputBlur}
          inputAriaLabel={`Minimum ${label.toLowerCase()}`}
          decrementAriaLabel={`Decrease minimum ${label.toLowerCase()}`}
          incrementAriaLabel={`Increase minimum ${label.toLowerCase()}`}
          disableDecrement={localMin <= min}
          disableIncrement={localMin >= localMax}
        />

        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
          to
        </Typography>

        <StepperInput
          value={localMax}
          step={step}
          isMobile={isMobile}
          onStep={handleStepMax}
          onInputChange={handleMaxInputChange}
          onInputBlur={handleMaxInputBlur}
          inputAriaLabel={`Maximum ${label.toLowerCase()}`}
          decrementAriaLabel={`Decrease maximum ${label.toLowerCase()}`}
          incrementAriaLabel={`Increase maximum ${label.toLowerCase()}`}
          disableDecrement={localMax <= localMin}
          disableIncrement={localMax >= max}
        />

        {unit && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {unit}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RangeInputWithSlider;
